import { motion } from "framer-motion";
import { Check } from "lucide-react";
import brand from "@/config/brand";

// Illustrative example mini-websites rendered inside the device frames.
// These are design demonstrations, not client projects.

const LandscapingSite = () => {
  const m = brand.mockupImages;
  const services = [
    { name: "Garden Design", img: m.landscapingThumb1 },
    { name: "Lawn Care", img: m.landscapingThumb2 },
    { name: "Stonework", img: m.landscapingHero },
  ];
  return (
    <div className="h-full w-full bg-white flex flex-col relative" data-testid="mockup-landscaping-site">
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-2.5 bg-charcoal/85 backdrop-blur-sm">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-turquoise" />
          <span className="font-serif text-[11px] text-cream">Evergreen Landscaping</span>
        </span>
        <span className="hidden sm:flex items-center gap-3 text-[8px] font-sans uppercase tracking-[0.14em] text-cream/70">
          <span>Services</span>
          <span>Gallery</span>
          <span>About</span>
        </span>
        <span className="bg-turquoise text-charcoal text-[8px] font-sans font-semibold px-2.5 py-1 rounded-full">
          Get a Quote
        </span>
      </div>
      <div className="relative h-[56%] shrink-0">
        <img src={m.landscapingHero} alt="Example landscaping website hero" className="w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-charcoal/35" />
        <div className="absolute bottom-3 left-24 sm:left-28 right-4">
          <p className="font-serif text-cream text-lg sm:text-xl leading-[1.15]">
            Landscapes Crafted
            <br />
            With <span className="italic text-turquoise">Care</span>
          </p>
          <p className="text-[8px] font-sans uppercase tracking-[0.2em] text-cream/85 mt-1.5">
            Design · Build · Maintain
          </p>
        </div>
      </div>
      <div className="flex-1 bg-cream px-4 py-3 min-h-0">
        <p className="text-[7px] font-sans uppercase tracking-[0.22em] font-semibold text-turquoise-deep">
          Our Services
        </p>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {services.map((s) => (
            <div key={s.name} className="rounded-md overflow-hidden bg-white shadow-sm">
              <img src={s.img} alt="" className="w-full h-7 sm:h-9 object-cover" loading="lazy" />
              <p className="text-[7px] sm:text-[8px] font-sans font-medium text-charcoal px-1.5 py-1 text-center">
                {s.name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PressureWashingSite = () => {
  const m = brand.mockupImages;
  return (
    <div className="h-full w-full bg-white flex flex-col relative" data-testid="mockup-pressure-site">
      <span className="absolute top-1.5 left-1/2 -translate-x-1/2 w-10 h-1.5 rounded-full bg-charcoal/90 z-20" />
      <div className="relative h-[36%] shrink-0">
        <img src={m.pressureHero} alt="Example pressure washing website hero" className="w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/50 via-transparent to-transparent" />
        <div className="absolute top-3.5 inset-x-0 flex items-center justify-between px-3">
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-turquoise" />
            <span className="font-serif text-[9px] text-cream">Crystal Clean</span>
          </span>
          <span className="flex flex-col gap-[2px]">
            <span className="w-3 h-[1.5px] bg-cream" />
            <span className="w-3 h-[1.5px] bg-cream" />
          </span>
        </div>
      </div>
      <div className="flex-1 px-3 pt-2.5 pb-3 flex flex-col min-h-0">
        <p className="text-[6px] font-sans uppercase tracking-[0.2em] font-semibold text-turquoise-deep">
          Pressure Washing
        </p>
        <p className="font-serif text-[14px] text-charcoal leading-[1.15] mt-1">
          A Cleaner Home,
          <br />
          Instantly.
        </p>
        <p className="text-[7.5px] font-sans text-taupe leading-snug mt-1">
          House washing, driveways, decks & more.
        </p>
        <span className="mt-2.5 block bg-turquoise text-charcoal text-[8px] font-sans font-semibold text-center py-2 rounded-full shadow-md">
          Get a Free Estimate
        </span>
        <div className="mt-2.5 space-y-1.5">
          {["House Washing", "Driveway & Concrete"].map((s) => (
            <span key={s} className="flex items-center gap-1.5 text-[7.5px] font-sans text-charcoal/80">
              <Check size={8} className="text-turquoise-deep" strokeWidth={3} />
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const DeviceMockup = ({ rotateX, rotateY }) => (
  <motion.div
    data-testid="hero-device-mockups"
    className="relative"
    style={{ rotateX, rotateY, transformPerspective: 1200 }}
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
  >
    <div className="absolute -inset-6 rounded-[2rem] border border-turquoise/25 pointer-events-none" aria-hidden="true" />

    {/* Desktop frame */}
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      className="relative bg-charcoal rounded-2xl p-2.5 shadow-[0_40px_80px_-20px_rgba(32,32,32,0.45)]"
    >
      <div className="flex items-center gap-1.5 px-2 pb-2">
        <span className="w-2.5 h-2.5 rounded-full bg-turquoise/80" />
        <span className="w-2.5 h-2.5 rounded-full bg-champagne/60" />
        <span className="w-2.5 h-2.5 rounded-full bg-cream/25" />
        <span className="ml-3 flex-1 h-5 rounded-md bg-cream/10" />
      </div>
      <div className="rounded-lg overflow-hidden aspect-[16/10] bg-white">
        <LandscapingSite />
      </div>
    </motion.div>

    {/* Phone frame */}
    <motion.div
      animate={{ y: [0, 10, 0] }}
      transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
      className="absolute -bottom-10 -left-4 sm:-left-10 w-24 sm:w-32 bg-charcoal rounded-[1.6rem] p-1.5 shadow-[0_30px_60px_-15px_rgba(32,32,32,0.5)] border border-champagne/20"
    >
      <div className="rounded-[1.2rem] overflow-hidden aspect-[9/18] bg-white">
        <PressureWashingSite />
      </div>
    </motion.div>

    {/* Turquoise accent chip */}
    <motion.div
      animate={{ y: [0, -12, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      className="absolute -top-6 -right-3 sm:-right-6 bg-turquoise text-charcoal rounded-xl px-4 py-3 shadow-xl"
    >
      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-charcoal/70">Mobile-Ready</p>
      <p className="font-serif text-sm sm:text-base">Every screen, covered</p>
    </motion.div>
  </motion.div>
);

export default DeviceMockup;
